import { useState, useEffect, useCallback } from "react";
import { apiClient, useAuth } from "../../App";
import { toast } from "sonner";
import { Calendar, Clock, Plus, Check, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";

export default function ArtistTimeOff() {
  const { user } = useAuth();
  const [balance, setBalance] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [newRequest, setNewRequest] = useState({
    leave_type: "PTO",
    start_date: "",
    end_date: "",
    notes: ""
  });

  const fetchData = useCallback(async () => {
    try {
      const [balanceRes, requestsRes] = await Promise.all([
        apiClient.get("/artist/my-pto-balance"),
        apiClient.get("/artist/my-time-off")
      ]);
      setBalance(balanceRes.data);
      setRequests(requestsRes.data || []);
    } catch (error) {
      console.error("Failed to fetch time off data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmitRequest = async () => {
    if (!newRequest.start_date || !newRequest.end_date) {
      toast.error("Please select start and end dates");
      return;
    }

    try {
      await apiClient.post("/artist/request-time-off", newRequest);
      toast.success("Time off request submitted");
      setShowRequestDialog(false);
      setNewRequest({ leave_type: "PTO", start_date: "", end_date: "", notes: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "approved": return <Badge className="bg-green-100 text-green-700">Approved</Badge>;
      case "pending": return <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>;
      case "rejected": return <Badge className="bg-red-100 text-red-700">Rejected</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="artist-time-off">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-light" style={{ fontFamily: 'var(--font-sans)' }}>
          Time Off
        </h1>
        <Button size="sm" onClick={() => setShowRequestDialog(true)}>
          <Plus className="w-4 h-4 mr-1.5" />
          Request Time Off
        </Button>
      </div>

      {/* PTO Balance Card */}
      {balance && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* PTO Balance */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                PTO Balance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-secondary/30 rounded-lg">
                  <p className="text-2xl font-light text-primary">{balance.available_days || 0}</p>
                  <p className="text-sm text-muted-foreground">Available</p>
                </div>
                <div className="text-center p-4 bg-secondary/30 rounded-lg">
                  <p className="text-2xl font-light text-orange-600">{balance.used_days || 0}</p>
                  <p className="text-sm text-muted-foreground">Used</p>
                </div>
                <div className="text-center p-4 bg-secondary/30 rounded-lg">
                  <p className="text-2xl font-light">{balance.pending_days || 0}</p>
                  <p className="text-sm text-muted-foreground">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* National Holidays Balance */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-amber-500"></span>
                National Holidays
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-secondary/30 rounded-lg">
                  <p className="text-2xl font-light text-amber-600">{balance.national_holiday_remaining || 12}</p>
                  <p className="text-sm text-muted-foreground">Remaining</p>
                </div>
                <div className="text-center p-4 bg-secondary/30 rounded-lg">
                  <p className="text-2xl font-light text-orange-600">{balance.national_holiday_used || 0}</p>
                  <p className="text-sm text-muted-foreground">Used</p>
                </div>
                <div className="text-center p-4 bg-secondary/30 rounded-lg">
                  <p className="text-2xl font-light">{balance.national_holiday_pending || 0}</p>
                  <p className="text-sm text-muted-foreground">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Time Off Requests */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">My Requests</CardTitle>
        </CardHeader>
        <CardContent>
          {requests.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Calendar className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No time off requests</p>
            </div>
          ) : (
            <div className="space-y-3">
              {requests.map(request => (
                <div key={request.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      request.leave_type === "PTO" ? "bg-blue-100" :
                      request.leave_type === "National Holiday" ? "bg-amber-100" :
                      request.leave_type === "Sick" ? "bg-red-100" : "bg-gray-100"
                    }`}>
                      <Calendar className={`w-5 h-5 ${
                        request.leave_type === "PTO" ? "text-blue-600" :
                        request.leave_type === "National Holiday" ? "text-amber-600" :
                        request.leave_type === "Sick" ? "text-red-600" : "text-gray-600"
                      }`} />
                    </div>
                    <div>
                      <p className="font-medium">{request.leave_type}</p>
                      <p className="text-sm text-muted-foreground">
                        {request.start_date} → {request.end_date}
                      </p>
                    </div>
                  </div>
                  {getStatusBadge(request.status)}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Request Dialog */}
      <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Time Off</DialogTitle>
            <DialogDescription>Submit a new time off request for approval</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={newRequest.leave_type} onValueChange={(v) => setNewRequest({ ...newRequest, leave_type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PTO">PTO</SelectItem>
                  <SelectItem value="National Holiday">National Holiday</SelectItem>
                  <SelectItem value="Sick">Sick Leave</SelectItem>
                  <SelectItem value="Personal">Personal</SelectItem>
                  <SelectItem value="Unpaid">Unpaid Leave</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input 
                  type="date" 
                  value={newRequest.start_date}
                  onChange={(e) => setNewRequest({ ...newRequest, start_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input 
                  type="date" 
                  value={newRequest.end_date}
                  onChange={(e) => setNewRequest({ ...newRequest, end_date: e.target.value })}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea 
                placeholder="Any additional notes..."
                value={newRequest.notes}
                onChange={(e) => setNewRequest({ ...newRequest, notes: e.target.value })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRequestDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmitRequest}>Submit Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
